import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api, { trackFunnel } from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { openRazorpayCheckout } from '../utils/razorpay';
import { 
  FileCheck2, FolderKanban, Plus, LogOut, FileText, CheckSquare, Play, 
  Sparkles, TrendingUp, AlertCircle, CheckCircle2, Clock, 
  FolderOpen, BarChart3, Activity, Target, Zap, ArrowRight, Check,
  AlertTriangle, ShieldCheck, TrendingDown, Rocket, X, ChevronRight, Crown, Mail, Bell, CreditCard
} from 'lucide-react';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { logout, user, refreshUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [allStats, setAllStats] = useState({});
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    loadProjects();
    loadInsights();
    refreshUser();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
      // Load stats for all projects
      const statsPromises = response.data.map(async (project) => {
        try {
          const statsRes = await api.get(`/projects/${project.id}/stats`);
          return { id: project.id, stats: statsRes.data };
        } catch {
          return { id: project.id, stats: null };
        }
      });
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setAllStats(statsMap);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async () => {
    try {
      const response = await api.get('/dashboard/insights');
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to load insights');
    }
  };

  // Calculate aggregate stats using latest run metrics
  const aggregateStats = useMemo(() => {
    const stats = Object.values(allStats).filter(Boolean);
    
    // Calculate latest pass rate (average of latest pass rates from each project)
    const projectsWithLatestData = stats.filter(s => s?.latest_pass_rate !== undefined && s?.test_runs > 0);
    const latestPassRate = projectsWithLatestData.length > 0
      ? Math.round(projectsWithLatestData.reduce((sum, s) => sum + (s?.latest_pass_rate || 0), 0) / projectsWithLatestData.length)
      : 0;
    
    // Calculate overall pass rate (average of overall pass rates)
    const projectsWithOverall = stats.filter(s => s?.overall_pass_rate !== undefined && s?.test_runs > 0);
    const overallPassRate = projectsWithOverall.length > 0
      ? Math.round(projectsWithOverall.reduce((sum, s) => sum + (s?.overall_pass_rate || 0), 0) / projectsWithOverall.length)
      : 0;
    
    return {
      totalProjects: projects.length,
      totalTestCases: stats.reduce((sum, s) => sum + (s?.test_cases || 0), 0),
      activeTestRuns: stats.reduce((sum, s) => sum + (s?.test_runs || 0), 0),
      // PRIMARY: Latest pass rate
      latestPassRate,
      // SECONDARY: Overall pass rate
      overallPassRate,
      // Backward compatibility
      passRate: latestPassRate,
      totalFailed: stats.reduce((sum, s) => sum + (s?.latest_failed || s?.failed || 0), 0),
      inProgressRuns: stats.filter(s => s?.test_runs > 0).length,
    };
  }, [projects, allStats]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get user initials
  const userInitials = useMemo(() => {
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  }, [user]);

  // Generate dynamic AI insight message based on LATEST run data
  const getAIInsight = () => {
    if (!insights) return null;
    
    const { summary, risks } = insights;
    
    // Priority 1: Failed tests in LATEST runs need immediate attention
    if (summary.latest_failed > 0) {
      return {
        type: 'warning',
        icon: AlertTriangle,
        color: 'amber',
        message: `${summary.latest_failed} test case${summary.latest_failed !== 1 ? 's' : ''} failed in latest runs. Immediate attention needed.`
      };
    }
    
    // Priority 2: Blocked tests in LATEST runs need attention
    if (summary.latest_blocked > 0) {
      return {
        type: 'warning',
        icon: AlertCircle,
        color: 'amber',
        message: `${summary.latest_blocked} test case${summary.latest_blocked !== 1 ? 's' : ''} blocked in latest runs. Review blockers.`
      };
    }
    
    // Priority 3: Low pass rate warning in LATEST runs (below 70%)
    if (summary.latest_pass_rate < 70 && summary.latest_pass_rate > 0) {
      return {
        type: 'error',
        icon: TrendingDown,
        color: 'red',
        message: `Latest run pass rate is ${summary.latest_pass_rate}%. Quality risk detected.`
      };
    }
    
    // Priority 4: Incomplete execution in LATEST runs
    if (summary.latest_not_executed > 0) {
      return {
        type: 'info',
        icon: Clock,
        color: 'blue',
        message: `${summary.latest_not_executed} test${summary.latest_not_executed !== 1 ? 's' : ''} pending execution. Continue test run.`
      };
    }
    
    // Priority 5: No test runs started
    if (summary.total_test_runs === 0 && summary.total_projects > 0) {
      return {
        type: 'info',
        icon: Rocket,
        color: 'blue',
        message: 'No test runs yet. Start execution to track QA progress.'
      };
    }
    
    // Priority 6: All good - high pass rate in LATEST runs
    if (summary.latest_pass_rate >= 70 && summary.latest_executed > 0) {
      return {
        type: 'success',
        icon: ShieldCheck,
        color: 'emerald',
        message: `Latest run pass rate is ${summary.latest_pass_rate}%. All systems healthy!`
      };
    }
    
    // Default message
    return {
      type: 'info',
      icon: Sparkles,
      color: 'blue',
      message: `You have ${summary.total_projects} active project${summary.total_projects !== 1 ? 's' : ''}. ${summary.total_test_runs > 0 ? `${summary.total_test_runs} test run${summary.total_test_runs !== 1 ? 's' : ''} tracked.` : ''}`
    };
  };

  const aiInsight = getAIInsight();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Enhanced Header */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur-xl sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl shadow-lg shadow-blue-600/20">
                <FileCheck2 className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-slate-900 tracking-tight">
                TestGenie AI
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/pricing')} className="text-sm text-slate-500 hover:text-blue-600 font-medium transition-colors" data-testid="nav-pricing-link">
                Pricing
              </button>
              {/* User Avatar */}
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold shadow-md">
                {userInitials}
              </div>
              {/* Logout Button - Icon + Text */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={handleLogout}
                      className="gap-2 px-4 py-2 h-auto text-slate-600 border-slate-200 rounded-lg hover:bg-slate-100 hover:text-slate-900 hover:border-slate-300 transition-all duration-200"
                      data-testid="logout-button"
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="font-medium">Logout</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Sign out of your account</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Welcome + AI Insights Section */}
        <div className="mb-8 flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 tracking-tight mb-1">
              Welcome back 👋
            </h2>
            <p className="text-slate-500">
              Here's your QA activity overview
            </p>
          </div>
          
          {/* Dynamic AI Insight Card */}
          {aiInsight && projects.length > 0 && (
            <AIInsightCard insight={aiInsight} />
          )}
        </div>

        {/* Email Verification Banner */}
        {user && user.email_verified === false && (
          <div className="mb-4 p-3 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg" data-testid="email-verify-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <span className="text-sm text-amber-800">Please verify your email address. Check your inbox for the verification link.</span>
            </div>
            <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="resend-verify-btn"
              onClick={async () => { try { await api.post('/resend-verification'); toast.success('Verification email sent!'); } catch { toast.error('Failed to send. Try again.'); } }}>
              Resend
            </Button>
          </div>
        )}

        {/* Current Plan Banner */}
        <Card className="mb-8 p-4 flex items-center justify-between bg-slate-50 border-slate-200" data-testid="plan-banner">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Crown className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <span className="text-sm font-semibold text-slate-900">{user?.plan === 'pro' ? 'Pro Plan' : 'Free Plan'}</span>
              <span className="text-xs text-slate-500 ml-2">{user?.plan === 'pro' ? '500 Requirements · 2000 Scenarios · 5000 Test Cases' : '50 Requirements · 200 Scenarios · 500 Test Cases'}</span>
            </div>
          </div>
          {user?.plan === 'pro' ? (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="pro-plan-badge">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Pro Plan Active</span>
            </div>
          ) : (
            <Button size="sm" onClick={() => { if (!user) { toast.error('Please login to continue with upgrade'); navigate('/login'); return; } trackFunnel('upgrade_clicked', { source: 'dashboard' }); setShowUpgradeModal(true); }} className="gap-1.5 bg-blue-600 hover:bg-blue-500 text-white" data-testid="upgrade-plan-button">
              <Zap className="h-3.5 w-3.5" /> Upgrade to Pro
            </Button>
          )}
        </Card>

        {/* Stats Overview Cards */}
        {projects.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatsCard 
              icon={FolderOpen} 
              value={aggregateStats.totalProjects} 
              label="Total Projects" 
              color="blue"
            />
            <StatsCard 
              icon={CheckSquare} 
              value={aggregateStats.totalTestCases} 
              label="Total Test Cases" 
              color="indigo"
            />
            <StatsCard 
              icon={Activity} 
              value={aggregateStats.activeTestRuns} 
              label="Active Test Runs" 
              color="purple"
            />
            <PassRateCard 
              latestPassRate={aggregateStats.latestPassRate}
              overallPassRate={aggregateStats.overallPassRate}
              hasTestRuns={aggregateStats.activeTestRuns > 0}
            />
          </div>
        )}

        {/* Risk Alerts + Recent Activity + Smart Suggestions Grid */}
        {projects.length > 0 && insights && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Risk Alerts */}
            <RiskAlertsCard risks={insights.risks} />
            
            {/* Recent Activity */}
            <RecentActivityCard activities={insights.activities} />
            
            {/* Smart Suggestions */}
            <SmartSuggestionsCard suggestions={insights.suggestions} />
          </div>
        )}

        {/* Workspace-level Test Effort Estimation */}
        {projects.length > 0 && insights?.effort?.total_testcases > 0 && (
          <EffortEstimationCard effort={insights.effort} />
        )}

        {/* Projects Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-1">
                Your Projects
              </h3>
              <p className="text-sm text-slate-500">
                Manage your QA documentation projects
              </p>
            </div>
            <Button
              onClick={() => navigate('/projects/new')}
              className="gap-2 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 text-white font-semibold rounded-xl shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-300 active:scale-[0.98]"
              data-testid="create-project-button"
            >
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6 animate-pulse bg-white rounded-xl border-0 shadow-sm">
                <div className="h-6 bg-slate-200 rounded mb-4" />
                <div className="h-4 bg-slate-200 rounded mb-2" />
                <div className="h-4 bg-slate-200 rounded w-2/3" />
              </Card>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onCreateProject={() => navigate('/projects/new')} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard 
                key={project.id} 
                project={project} 
                stats={allStats[project.id]}
              />
            ))}
          </div>
        )}
      </main>

      {/* Upgrade to Pro Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Crown className="h-5 w-5 text-blue-600" /> Upgrade to Pro
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Unlock higher limits and priority AI processing for <span className="font-semibold text-slate-900">₹499/month</span>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="gap-2" data-testid="upgrade-cancel-btn">
              Cancel
            </Button>
            <Button onClick={() => {
              setShowUpgradeModal(false);
              openRazorpayCheckout({
                userName: user?.full_name || '',
                userEmail: user?.email || '',
                onSuccess: () => {
                  toast.success('Payment successful');
                  refreshUser();
                },
                onFailure: () => {
                  toast.error('Payment failed, please try again');
                },
              });
            }} className="gap-2 bg-blue-600 hover:bg-blue-500" data-testid="upgrade-pay-btn">
              <CreditCard className="h-4 w-4" /> Pay ₹499 Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dynamic AI Insight Card Component
const AIInsightCard = ({ insight }) => {
  const colorConfig = {
    amber: {
      bg: 'bg-amber-500/20',
      text: 'text-amber-400',
      glow: 'from-amber-500 to-orange-500',
      pulse: true
    },
    red: {
      bg: 'bg-red-500/20',
      text: 'text-red-400',
      glow: 'from-red-500 to-pink-500',
      pulse: true
    },
    blue: {
      bg: 'bg-blue-500/20',
      text: 'text-blue-400',
      glow: 'from-blue-600 to-indigo-600',
      pulse: false
    },
    emerald: {
      bg: 'bg-emerald-500/20',
      text: 'text-emerald-400',
      glow: 'from-emerald-500 to-teal-500',
      pulse: false
    }
  };

  const config = colorConfig[insight.color] || colorConfig.blue;
  const Icon = insight.icon;

  return (
    <div className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${config.glow} rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-300 ${config.pulse ? 'animate-pulse' : ''}`} />
      <Card className="relative px-5 py-4 bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 border-0 rounded-xl">
        <div className="flex items-start gap-3">
          <div className={`p-2 ${config.bg} rounded-lg ${config.pulse ? 'animate-pulse' : ''}`}>
            <Icon className={`h-5 w-5 ${config.text}`} />
          </div>
          <div>
            <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-1">AI Insight</p>
            <p className="text-sm text-blue-50 max-w-xs leading-relaxed">
              {insight.message}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Workspace-level Test Effort Estimation Card
const EffortEstimationCard = ({ effort }) => {
  const b = effort.breakdown || {};
  const formatH = (h) => h < 1 ? `${Math.round(h * 60)}m` : `${h}h`;
  return (
    <Card className="p-5 mb-8 bg-gradient-to-br from-slate-50 to-blue-50/40 border-slate-200" data-testid="dashboard-effort-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 bg-blue-600 rounded-lg"><Clock className="h-4 w-4 text-white" /></div>
          <div>
            <h3 className="text-sm font-heading font-semibold text-slate-900">Test Execution Effort — Workspace</h3>
            <p className="text-xs text-slate-500 mt-0.5">Estimated manual execution time across all your projects</p>
          </div>
        </div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider">Auto-calculated</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="effort-total-tcs">{effort.total_testcases}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Total Test Cases</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-amber-600 tabular-nums" data-testid="effort-total-hours">{formatH(effort.total_hours)}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Total Effort</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-emerald-600 tabular-nums" data-testid="effort-person-days">{effort.person_days}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Person-Days (1 QA)</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-violet-600 tabular-nums" data-testid="effort-2qa-days">{effort.team_2qa_days}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">Calendar Days (2 QA)</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Complexity Mix</p>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-50 text-red-700">C: {b.Complex || 0}</span>
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-50 text-amber-700">M: {b.Medium || 0}</span>
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-600">S: {b.Simple || 0}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};


// Risk Alerts Card Component
const RiskAlertsCard = ({ risks }) => {
  const navigate = useNavigate();

  return (
    <Card className="p-5 bg-white rounded-2xl border-0 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-red-50 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-red-500" />
        </div>
        <h4 className="font-semibold text-slate-900">Risk Alerts</h4>
      </div>
      
      {risks.length === 0 ? (
        <div className="flex items-center gap-3 py-4">
          <div className="p-2 bg-emerald-50 rounded-full">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="text-sm text-slate-600">No risks detected. Your QA is in good shape.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {risks.map((risk, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer group"
              onClick={() => risk.project_id && navigate(`/projects/${risk.project_id}`)}
            >
              <div className={`p-1.5 rounded-lg ${risk.severity === 'high' ? 'bg-red-100' : 'bg-amber-100'}`}>
                <AlertCircle className={`h-3.5 w-3.5 ${risk.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 font-medium truncate">{risk.message}</p>
                <p className="text-xs text-slate-500 group-hover:text-blue-600 transition-colors">
                  {risk.project_name}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Recent Activity Card Component
const RecentActivityCard = ({ activities }) => {
  const navigate = useNavigate();

  const getActivityIcon = (type, iconName) => {
    switch (iconName) {
      case 'sparkles': return <Sparkles className="h-3.5 w-3.5 text-purple-500" />;
      case 'play': return <Play className="h-3.5 w-3.5 text-blue-500" />;
      case 'check': return <Check className="h-3.5 w-3.5 text-emerald-500" />;
      case 'x': return <X className="h-3.5 w-3.5 text-red-500" />;
      default: return <Activity className="h-3.5 w-3.5 text-slate-500" />;
    }
  };

  const getActivityBg = (iconName) => {
    switch (iconName) {
      case 'sparkles': return 'bg-purple-100';
      case 'play': return 'bg-blue-100';
      case 'check': return 'bg-emerald-100';
      case 'x': return 'bg-red-100';
      default: return 'bg-slate-100';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card className="p-5 bg-white rounded-2xl border-0 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-indigo-50 rounded-lg">
          <Activity className="h-4 w-4 text-indigo-500" />
        </div>
        <h4 className="font-semibold text-slate-900">Recent Activity</h4>
      </div>
      
      {activities.length === 0 ? (
        <div className="flex items-center gap-3 py-4">
          <div className="p-2 bg-slate-50 rounded-full">
            <Clock className="h-4 w-4 text-slate-400" />
          </div>
          <p className="text-sm text-slate-500">No recent activity yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map((activity, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 group cursor-pointer"
              onClick={() => activity.project_id && navigate(`/projects/${activity.project_id}`)}
            >
              <div className={`p-1.5 rounded-lg ${getActivityBg(activity.icon)}`}>
                {getActivityIcon(activity.type, activity.icon)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 truncate group-hover:text-blue-600 transition-colors">
                  {activity.message}
                </p>
                <p className="text-xs text-slate-400">
                  {activity.project_name} • {formatTimestamp(activity.timestamp)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Smart Suggestions Card Component
const SmartSuggestionsCard = ({ suggestions }) => {
  const navigate = useNavigate();

  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'create_project': return <FolderOpen className="h-4 w-4" />;
      case 'start_test_run': return <Play className="h-4 w-4" />;
      case 'execute_tests': return <CheckSquare className="h-4 w-4" />;
      default: return <Zap className="h-4 w-4" />;
    }
  };

  const handleSuggestionClick = (suggestion) => {
    switch (suggestion.type) {
      case 'create_project':
        navigate('/projects/new');
        break;
      case 'start_test_run':
      case 'execute_tests':
        if (suggestion.project_id) {
          navigate(`/projects/${suggestion.project_id}`);
        }
        break;
      default:
        break;
    }
  };

  return (
    <Card className="p-5 bg-white rounded-2xl border-0 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-amber-50 rounded-lg">
          <Zap className="h-4 w-4 text-amber-500" />
        </div>
        <h4 className="font-semibold text-slate-900">Smart Suggestions</h4>
      </div>
      
      {suggestions.length === 0 ? (
        <div className="flex items-center gap-3 py-4">
          <div className="p-2 bg-emerald-50 rounded-full">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="text-sm text-slate-600">You're all caught up!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {suggestions.map((suggestion, index) => (
            <div 
              key={index}
              className="p-3 rounded-xl bg-gradient-to-r from-slate-50 to-blue-50/50 hover:from-blue-50 hover:to-indigo-50/50 transition-all cursor-pointer group"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow transition-shadow">
                    {getSuggestionIcon(suggestion.type)}
                  </div>
                  <p className="text-sm text-slate-700 font-medium">{suggestion.message}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-100 gap-1 h-7"
                >
                  {suggestion.action}
                  <ArrowRight className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Stats Card Component
const StatsCard = ({ icon: Icon, value, label, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
    indigo: 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100',
    purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
    emerald: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100',
  };

  return (
    <Card className="group p-5 bg-white rounded-xl border-0 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl transition-colors ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-500">{label}</div>
        </div>
      </div>
    </Card>
  );
};

// Pass Rate Card Component with Latest + Overall
const PassRateCard = ({ latestPassRate, overallPassRate, hasTestRuns }) => {
  // Determine color based on latest pass rate
  const getPassRateColor = (rate) => {
    if (rate >= 80) return 'emerald';
    if (rate >= 60) return 'amber';
    return 'red';
  };
  
  const color = hasTestRuns ? getPassRateColor(latestPassRate) : 'slate';
  
  const colorClasses = {
    emerald: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100',
    amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-100',
    red: 'bg-red-50 text-red-600 group-hover:bg-red-100',
    slate: 'bg-slate-50 text-slate-600 group-hover:bg-slate-100',
  };

  return (
    <Card className="group p-5 bg-white rounded-xl border-0 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl transition-colors ${colorClasses[color]}`}>
          <Target className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">
              {hasTestRuns ? `${latestPassRate}%` : '—'}
            </span>
            {hasTestRuns && (
              <span className="text-xs font-medium text-slate-400">(Latest)</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Pass Rate</span>
            {hasTestRuns && latestPassRate !== overallPassRate && (
              <span className="text-xs text-slate-400">
                Overall: {overallPassRate}%
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Empty State Component
const EmptyState = ({ onCreateProject }) => {
  const benefits = [
    'AI-generated test cases',
    'Structured test plans',
    'Real-time execution tracking',
  ];

  return (
    <Card className="p-12 text-center bg-white rounded-2xl border-0 shadow-sm" data-testid="empty-state">
      {/* Illustration */}
      <div className="relative mx-auto w-24 h-24 mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-full blur-xl" />
        <div className="relative w-full h-full bg-gradient-to-br from-slate-100 to-slate-50 rounded-full flex items-center justify-center border border-slate-200">
          <Sparkles className="h-10 w-10 text-blue-600" />
        </div>
      </div>
      
      <h3 className="text-2xl font-heading font-bold text-slate-900 mb-2">
        Start Your First QA Project
      </h3>
      <p className="text-slate-500 mb-8 max-w-md mx-auto">
        Generate test cases, test plans, and execute QA workflows with AI
      </p>
      
      {/* Benefits */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-8">
        {benefits.map((benefit, index) => (
          <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-3 w-3 text-emerald-600" />
            </div>
            {benefit}
          </div>
        ))}
      </div>
      
      {/* CTA Button */}
      <Button
        onClick={onCreateProject}
        className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30 transition-all duration-300 active:scale-[0.98] px-8 py-6 text-base"
        data-testid="empty-create-project-button"
      >
        <Plus className="h-5 w-5" />
        Create Your First Project
        <ArrowRight className="h-4 w-4 ml-1" />
      </Button>
    </Card>
  );
};

// Project Card Component
const ProjectCard = ({ project, stats }) => {
  const navigate = useNavigate();

  // Determine health status based on LATEST run data (not historical)
  const getHealthStatus = () => {
    if (!stats) return 'neutral';
    
    // Use latest run status from backend
    const latestStatus = stats.latest_run_status;
    
    // Priority: Check LATEST run for failures/blocks first
    if (stats.latest_failed > 0) return 'attention';
    if (stats.latest_blocked > 0) return 'warning';
    
    // Then check latest run execution status
    if (latestStatus === 'completed' && stats.latest_failed === 0) return 'healthy';
    if (latestStatus === 'in_progress') return 'progress';
    
    // Fallback to legacy fields for backward compatibility
    if (stats.execution_status === 'not_started' && stats.test_cases > 0) return 'ready';
    if (stats.test_runs > 0 && stats.latest_not_run > 0) return 'progress';
    
    return 'neutral';
  };

  const healthStatus = getHealthStatus();
  const healthConfig = {
    healthy: { color: 'bg-emerald-500', label: 'Completed - All Pass', icon: CheckCircle2 },
    warning: { color: 'bg-amber-500', label: 'Has Blocked Tests', icon: AlertTriangle },
    progress: { color: 'bg-blue-500', label: 'In Progress', icon: Clock },
    ready: { color: 'bg-indigo-500', label: 'Ready to Execute', icon: Play },
    attention: { color: 'bg-red-500', label: 'Has Failures', icon: AlertCircle },
    neutral: { color: 'bg-slate-400', label: 'No Data', icon: Activity },
  };

  const health = healthConfig[healthStatus];

  return (
    <Card
      className="group p-6 bg-white rounded-xl border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
      onClick={() => navigate(`/projects/${project.id}`)}
      data-testid={`project-card-${project.id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
            {project.name}
          </h3>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium">
              {project.platform}
            </span>
            {project.domain && (
              <span className="text-xs text-slate-400">{project.domain}</span>
            )}
          </div>
        </div>
        {/* Health Indicator */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className={`w-3 h-3 rounded-full ${health.color} ring-4 ring-opacity-20 ${health.color.replace('bg-', 'ring-')}`} />
            </TooltipTrigger>
            <TooltipContent side="left">
              <div className="flex items-center gap-2">
                <health.icon className="h-3.5 w-3.5" />
                <span>{health.label}</span>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Divider */}
      <div className="border-t border-slate-100 my-4" />

      {/* Stats Grid */}
      {stats ? (
        <div className="grid grid-cols-3 gap-3">
          <StatItem 
            icon={FileText} 
            value={stats.requirements} 
            label="Requirements" 
          />
          <StatItem 
            icon={CheckSquare} 
            value={stats.test_cases} 
            label="Test Cases" 
          />
          <StatItem 
            icon={Play} 
            value={stats.test_runs} 
            label="Test Runs" 
          />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="text-center animate-pulse">
              <div className="h-4 w-4 mx-auto mb-2 bg-slate-200 rounded" />
              <div className="h-5 w-8 mx-auto mb-1 bg-slate-200 rounded" />
              <div className="h-3 w-12 mx-auto bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

// Stat Item Component
const StatItem = ({ icon: Icon, value, label }) => (
  <div className="text-center group/stat">
    <div className="flex items-center justify-center mb-1.5">
      <Icon className="h-4 w-4 text-slate-400 group-hover/stat:text-blue-500 transition-colors" />
    </div>
    <div className="text-lg font-semibold text-slate-900">
      {value}
    </div>
    <div className="text-xs text-slate-400 uppercase tracking-wider">
      {label.split(' ')[0]}
    </div>
  </div>
);